// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Selector.tsx
 * @input Uses React, StyleX, usePopover, useTooltip, Icon, InputGroupContext,
 *   and Selector positioning hooks
 * @output Exports Selector component
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Selector/Selector.doc.mjs
 * - /packages/core/src/Selector/Selector.test.tsx
 * - /packages/core/src/Selector/index.ts
 * - /apps/storybook/stories/InputGroup.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Selector/ (showcase blocks)
 */
import React, { useCallback, useEffect, useId, useMemo, useOptimistic, useRef, useState, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import { useTooltip } from "../Tooltip/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { useIndicator } from "../Indicator/index.js";
import { Field, InputClearButton, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputWrapperStyles } from "../Field/index.js";
import { Divider } from "../Divider/index.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { Spinner } from "../Spinner/index.js";
import { PanelSearchInput } from "../Field/PanelSearchInput.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, fontWeightVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { isOptionData, isDivider, isSection, normalizeOption, getSelectableOptions } from "./utils.js";
import { useCombobox, useSelectedItemOffset } from "./hooks.js";
import { useTypeahead } from "../hooks/useTypeahead.js";
import { SelectorOption } from "./SelectorOption.js";
import { getInputARIA, mergeProps } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { focusOutlineStyles } from "../utils/focusOutline.stylex.js";
import { stableClassName } from "../naming.js";
import { groupStyles } from "../InputGroup/groupStyles.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  triggerContainer: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib xdk3u5w",
    kLWn49: "x1kq96og",
    kMwMTN: "x1tgivj0",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  triggerPlaceholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  triggerIcon: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  triggerIconRotation: {
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    k3nNDw: "x1g0ag68",
    $$css: true
  },
  triggerIconOpen: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  triggerGhost: {
    kzqmXN: "x14atkfc",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kGVxlE: "x1gnnqk1 x1irk71m x15ysqaf",
    k63SB2: "x1e4wzip",
    k1ekBW: "xrafxwg",
    k3aq6I: "x3oybdh xk4oym4",
    $$css: true
  },
  triggerGhostDisabled: {
    kKwaWg: "x18o3ruo",
    k3aq6I: "x1c071of x1pdlv7q",
    $$css: true
  },
  statusButton: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    kaIpWk: "xh6dtrn",
    $$css: true
  },
  searchRowInput: {
    kg3NbH: "x2hg6jq",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    $$css: true
  },
  divider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  },
  item: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    kmVPX3: "xlsj2fj",
    kaIpWk: "xh6dtrn",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w",
    kkrTdU: "x1ypdohk",
    k9WMMc: "x1yc453h",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  itemHighlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  itemSelected: {
    k63SB2: "x1e4wzip",
    $$css: true
  },
  itemDisabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};

/**
 * Size-specific overrides for dropdown list items.
 * Matches the pattern used by DropdownMenuItem so that
 * an `sm` selector renders compact list items, `md`/`lg` use
 * the base padding defined in `styles.item`.
 */
const itemSizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    $$css: true
  },
  lg: {
    $$css: true
  }
};
const STATUS_ICON_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_ICON_COLOR_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_BUTTON_LABEL_KEY = {
  warning: '@astryx.input.statusButton.warning',
  error: '@astryx.input.statusButton.error',
  success: '@astryx.input.statusButton.success'
};

/**
 * Without `hasClear`, the selector always has a string value (or undefined for placeholder).
 * With `hasClear`, the value can be `null` and onChange receives `null` on clear.
 */

/**
 * Default option renderer
 */
function DefaultOption({
  option
}) {
  return /*#__PURE__*/_jsx(SelectorOption, {
    icon: option.icon,
    label: option.label ?? option.value
  });
}

// Case-insensitive substring match for a single option. The one predicate used
// by both the flat filter (count + keyboard nav) and the grouped renderer, so
// what is shown while searching stays in lockstep with the announced count.
function optionMatchesQuery(option, query) {
  if (!query) {
    return true;
  }
  return (option.label ?? option.value).toLowerCase().includes(query.toLowerCase());
}

// Case-insensitive substring filter over the selectable options. Shared by the
// `filteredItems` memo (rendering) and the search-change handler, which needs
// the count for the *next* query synchronously to announce it exactly once per
// keystroke rather than reacting to state in an effect.
function filterOptionsByQuery(items, query) {
  if (!query) {
    return items;
  }
  return items.filter(item => optionMatchesQuery(item, query));
}

/**
 * A selector/dropdown component for choosing from a list of options.
 *
 * @example
 * ```
 * <Selector
 *   label="Fruit"
 *   options={['Apple', 'Banana', 'Orange']}
 *   value={fruit}
 *   onChange={setFruit}
 *   placeholder="Select a fruit..."
 * />
 * ```
 */
export function Selector(props) {
  const t = useTranslator();
  const {
    label,
    isLabelHidden = false,
    description,
    isOptional = false,
    isRequired = false,
    isDisabled = false,
    disabledMessage,
    options,
    value,
    onChange,
    changeAction,
    isLoading = false,
    placeholder: placeholderFromProps,
    size: sizeProp,
    variant = 'input',
    status,
    statusVariant = 'attached',
    labelTooltip,
    startIcon,
    htmlName,
    renderOption,
    indicatorPosition = 'end',
    hasSearch = false,
    searchPlaceholder: searchPlaceholderFromProps,
    placement,
    isDefaultOpen = false,
    'data-testid': testId,
    width,
    xstyle,
    className,
    style,
    hasClear: hasClearProp,
    ...rest
  } = props;
  const placeholder = placeholderFromProps ?? t('@astryx.selector.placeholder');
  const searchPlaceholder = searchPlaceholderFromProps ?? t('@astryx.selector.searchPlaceholder');
  const hasClear = hasClearProp === true;
  const size = useSize(sizeProp, 'md');
  const effectiveStatusVariant = variant === 'ghost' && statusVariant === 'attached' ? 'detached' : statusVariant;

  // Normalize null to undefined for internal use (null is the clear sentinel)
  const normalizedValue = value === null ? undefined : value;
  const triggerId = useId();
  const listboxId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const inputLabelId = useId();
  const searchId = useId();
  // Measure from the same outer control that usePopover anchors to; using the
  // shorter inner button makes every size's selected row land too low.
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const inputGroup = useInputGroup();
  const [searchQuery, setSearchQuery] = useState('');
  // A typed query shows the search row's clear (✕) button, which becomes
  // the next tab stop after the search input.
  const hasQuery = searchQuery.length > 0;
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(normalizedValue);
  const isBusy = isLoading || optimisticValue !== normalizedValue;
  const announce = useAnnounce();

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the trigger container (which already exists)
  // and the trigger button stays perceivable via aria-disabled instead of the
  // disabled attribute. Activation is blocked by the isDisabled guards in
  // useCombobox (onTriggerClick / onKeyDown).
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the trigger button, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusTooltip = useTooltip({
    placement: 'above',
    isEnabled: effectiveStatusVariant === 'tooltip' && !!status?.message
  });
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelId, [description ? descriptionId : null, !inputGroup && effectiveStatusVariant !== 'tooltip' && status?.message ? statusMessageId : null, effectiveStatusVariant === 'tooltip' && status?.message ? statusTooltip.describedBy : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);

  // Flatten options for keyboard navigation
  const selectableItems = useMemo(() => getSelectableOptions(options), [options]);

  // Filter items by search query
  const filteredItems = useMemo(() => filterOptionsByQuery(selectableItems, searchQuery), [selectableItems, searchQuery]);

  // Find selected item and its index for positioning
  const selectedItemIndex = useMemo(() => {
    return selectableItems.findIndex(item => item.value === optimisticValue);
  }, [selectableItems, optimisticValue]);
  const selectedItem = useMemo(() => {
    return selectedItemIndex >= 0 ? selectableItems[selectedItemIndex] : undefined;
  }, [selectableItems, selectedItemIndex]);

  // Ref for listbox to measure selected item position
  const listboxRef = useRef(null);

  // Typeahead is defined below (it needs the popover), but closing and clearing
  // must drop its pending buffer — otherwise a stale prefix survives the reset
  // window and poisons the next keystroke ("Dog" then "c" would search "dc").
  const resetTypeaheadRef = useRef(() => {});

  // Layer for dropdown positioning
  const handleLayerHide = useCallback(() => {
    setSearchQuery('');
    resetTypeaheadRef.current();
    // Clear any lingering result count when the popover closes so stale status
    // text does not linger in the a11y tree.
    announce('');
    triggerRef.current?.focus();
  }, [announce]);
  const popover = usePopover({
    onHide: handleLayerHide,
    hasLightDismiss: true,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="listbox" is the exposed semantics; the trigger
    // keeps DOM focus, so wrapping it in a modal dialog would misrepresent it.
    role: 'none',
    // The theme target belongs on the SURFACE that paints the popup, which
    // `usePopover` owns — not on the scrolling list inside it.
    surfaceTarget: 'selector-popup'
  });

  // Open dropdown on mount when isDefaultOpen is true
  useEffect(() => {
    if (isDefaultOpen) {
      popover.show();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: isDefaultOpen is not reactive
  }, []);

  // Announce the filtered result count from the query-change handler (matching
  // BaseTypeahead) rather than a reactive effect: computing the count for the
  // next query here fires the announcement exactly once per keystroke and does
  // not re-speak on unrelated re-renders.
  const handleSearchChange = useCallback(nextQuery => {
    setSearchQuery(nextQuery);
    if (nextQuery.length === 0) {
      // Emptying the query clears the region rather than announcing a count.
      announce('');
      return;
    }
    const count = filterOptionsByQuery(selectableItems, nextQuery).length;
    announce(count === 0 ? 'No results found' : `${count} result${count === 1 ? '' : 's'}`);
  }, [announce, selectableItems]);

  // Calculate offset to position selected item over trigger. Explicit
  // placement opts out of the selector-specific overlay behavior and uses the
  // standard layer positioning API instead.
  const shouldOverlaySelectedItem = placement == null && !hasSearch;
  const {
    offset: rawOffset,
    isPositioned: rawIsPositioned
  } = useSelectedItemOffset({
    isOpen: popover.isOpen && shouldOverlaySelectedItem,
    selectedItemIndex,
    listboxId,
    listboxRef,
    anchorRef
  });
  const selectedItemOffset = shouldOverlaySelectedItem ? rawOffset : 0;
  const isPositioned = shouldOverlaySelectedItem ? rawIsPositioned : true;
  const popoverPlacement = placement ?? 'below';
  const popoverOffsetStyle = selectedItemOffset > 0 ? {
    marginBlockStart: `-${selectedItemOffset}px`
  } : undefined;

  // Clear the current value. Shared by the clear button and the keyboard
  // Delete/Backspace path so clearing is reachable without a mouse.
  const clearValue = useCallback(() => {
    resetTypeaheadRef.current();
    onChange?.(null);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(undefined);
        await changeAction(null);
      });
    }
  }, [onChange, changeAction, startTransition, setOptimisticValue]);

  // Type-to-find appends to the query rather than replacing it: characters
  // typed before focus reaches the search input must not be dropped.
  const appendSearchQuery = useCallback(char => {
    setSearchQuery(query => query + char);
  }, []);
  const commitValue = useCallback(newValue => {
    onChange?.(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [onChange, changeAction, startTransition, setOptimisticValue]);

  // Selector behavior (keyboard nav, selection)
  const {
    highlightedIndex,
    setHighlightedIndex,
    getItemId,
    onTriggerClick,
    onKeyDown,
    onItemSelect,
    onItemMouseEnter
  } = useCombobox({
    selectableItems: filteredItems,
    // The optimistic value, not the raw prop: with a pending changeAction the
    // prop still holds the old selection, so the popup would open with the
    // highlight on it and Delete/Backspace could clear a value the action has
    // already replaced.
    value: optimisticValue,
    isDisabled,
    isOpen: popover.isOpen,
    hasSearch,
    onOpen: useCallback(() => {
      popover.show();
      if (hasSearch) {
        requestAnimationFrame(() => {
          const input = searchRef.current;
          if (input) {
            input.focus();
            // When typing seeded the query, place the caret after it so the
            // user keeps typing where they left off.
            input.setSelectionRange(input.value.length, input.value.length);
          }
        });
      }
    }, [popover, hasSearch]),
    onClose: popover.hide,
    onSelect: commitValue,
    onClear: hasClear ? clearValue : undefined,
    onSearchSeed: appendSearchQuery,
    listboxId
  });

  // Type-to-select, shared with the other collections (menus, listboxes).
  // Open, it walks the highlight — aria-activedescendant announces each match.
  // Closed, it commits the match like a native select, which changes the value
  // without opening the popup or moving focus, so nothing else would prompt
  // assistive tech to re-read the trigger: announce it explicitly.
  const typeahead = useTypeahead({
    getItemLabels: () => selectableItems.map(item => item.label ?? item.value),
    isDisabled: index => selectableItems[index]?.disabled === true,
    // Cycle onward from the highlight when open, from the committed selection
    // when closed — the optimistic one, so a pending changeAction cannot strand
    // cycling on the first match. -1 means nothing is selected or highlighted,
    // which the hook reads as "search from the top".
    getCurrentIndex: () => popover.isOpen ? highlightedIndex : selectedItemIndex,
    onMatch: index => {
      const item = selectableItems[index];
      if (popover.isOpen) {
        setHighlightedIndex(index);
      } else if (item.value !== optimisticValue) {
        commitValue(item.value);
        announce(item.label ?? item.value);
      }
    }
  });
  resetTypeaheadRef.current = typeahead.reset;
  const handleTriggerKeyDown = useCallback(e => {
    // With hasSearch the query input owns typing, so type-to-select is off.
    if (!isDisabled && !hasSearch && typeahead.onKeyDown(e)) {
      e.preventDefault();
      return;
    }
    onKeyDown(e);
  }, [isDisabled, hasSearch, typeahead, onKeyDown]);

  // Keep the highlighted option visible during keyboard navigation. The
  // listbox is a fixed-height scroll container, so without this the virtual
  // cursor walks off-screen once navigation passes the visible window. Mirrors
  // CommandPaletteItem's scrollIntoView({block: 'nearest'}) behavior.
  useEffect(() => {
    if (!popover.isOpen || highlightedIndex < 0) {
      return;
    }
    document.getElementById(getItemId(highlightedIndex))?.scrollIntoView?.({
      block: 'nearest'
    });
  }, [popover.isOpen, highlightedIndex, getItemId]);

  // Handle clear button click
  const handleClear = useCallback(e => {
    e.stopPropagation(); // Don't open dropdown
    clearValue();
  }, [clearValue]);

  // Render search input
  const renderSearch = useCallback(() => {
    if (!hasSearch) {
      return null;
    }
    return /*#__PURE__*/_jsx(PanelSearchInput, {
      ref: searchRef,
      id: searchId
      // The search row is the panel's header: a magnifier, a borderless
      // input, and the shared clear (✕) button. It deliberately does NOT
      // render a bordered TextInput — the popup is already a bordered
      // surface, and a field inside it drew a second box within that box.
      ,
      label: t('@astryx.selector.searchOptions')
      // Same accessible name the TextInput's built-in clear produced
      // ("Clear Search options"), so the affordance keeps its name while its
      // chrome changes.
      ,
      clearLabel: t('@astryx.textInput.clearLabel', {
        label: t('@astryx.selector.searchOptions')
      }),
      ...themeProps('selector-search'),
      xstyle: variant !== 'ghost' && styles.searchRowInput
      // When hasSearch is set, focus moves into this input on open, so it —
      // not the trigger — must be the combobox that reports the highlighted
      // option via aria-activedescendant (comboboxes-4). A bare searchbox
      // left the highlight silent to screen readers.
      ,
      role: "combobox",
      "aria-expanded": popover.isOpen,
      "aria-controls": listboxId,
      "aria-autocomplete": "list",
      "aria-activedescendant": popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
      value: searchQuery,
      onValueChange: handleSearchChange,
      onContainerKeyDown: e => {
        // The clear (✕) button lives inside the row, after the input in DOM
        // order. When it is focused and the user tabs forward there is
        // nothing else in the popup, so dismiss it (Shift+Tab returns to the
        // input natively). Key events originating on the input are handled on
        // the input below; ignore them here so we don't double-dismiss.
        if (e.target === searchRef.current) {
          return;
        }
        if (e.key === 'Tab' && !e.shiftKey) {
          onKeyDown(e);
        }
      },
      onKeyDown: e => {
        // Arrow keys navigate options; Enter selects; Escape closes.
        // Home/End are left to the input for caret movement (APG editable
        // combobox); PageUp/PageDown are the sanctioned substitute for
        // jumping to the first/last option.
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'Escape') {
          onKeyDown(e);
          return;
        }
        // Tab: when a query is showing the clear (✕) button, forward-tab
        // moves focus to it (keeping the popup open) so the affordance is
        // keyboard-reachable. Every other Tab dismisses the popup as usual.
        if (e.key === 'Tab' && (e.shiftKey || !hasQuery)) {
          onKeyDown(e);
        }
      },
      placeholder: searchPlaceholder
    });
  }, [hasSearch, searchId, listboxId, searchQuery, hasQuery, searchPlaceholder, handleSearchChange, onKeyDown, popover.isOpen, highlightedIndex, getItemId, variant, t]);

  // The single-selection mark, resolved from the theme once per render. A
  // theme that maps `check` to another indicator (a radio, say) changes every
  // selected-option mark in the app through this one lookup.
  const SelectionMark = useIndicator('check');

  // Render an individual item
  const renderItem = useCallback((item, flatIndex) => {
    const isHighlighted = flatIndex === highlightedIndex;
    const isSelected = item.value === normalizedValue;

    /*
     * Rendered UNCONDITIONALLY, with the state passed down: the default
     * check draws nothing when unchecked, but a theme that replaces the
     * `check` indicator with a radio needs the unselected state to draw
     * its empty circle. `{isSelected && …}` would make that impossible.
     *
     * `selector-check` stays the stable target for the mark's position
     * in the row; the indicator owns what the mark looks like.
     */
    const mark = /*#__PURE__*/_jsx("span", {
      ...{
        className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s xgomdpo"
      },
      children: /*#__PURE__*/_jsx(SelectionMark, {
        state: isSelected ? 'checked' : 'unchecked',
        size: "sm",
        isDisabled: item.disabled ?? false,
        ...themeProps('selector-check')
      })
    });
    const optionContent = /*#__PURE__*/_jsx("span", {
      ...{
        className: "x78zum5 x6s0dn4 x1txdalj x98rzlu xeuugli"
      },
      children: renderOption ? renderOption(item) : /*#__PURE__*/_jsx(DefaultOption, {
        option: item
      })
    });
    const content = indicatorPosition === 'start' ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [mark, optionContent]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [optionContent, mark]
    });
    return /*#__PURE__*/_jsx("div", {
      id: getItemId(flatIndex),
      role: "option",
      "aria-selected": isSelected,
      "aria-disabled": item.disabled,
      onClick: () => onItemSelect(item),
      onMouseEnter: () => onItemMouseEnter(item, flatIndex),
      ...stylex.props(styles.item, itemSizeStyles[size], isHighlighted && styles.itemHighlighted, isSelected && styles.itemSelected, item.disabled && styles.itemDisabled),
      children: content
    }, item.value);
  }, [renderOption, indicatorPosition, highlightedIndex, size, normalizedValue, getItemId, onItemSelect, onItemMouseEnter, SelectionMark]);

  // Render all options (handling sections/dividers)
  const renderOptions = useCallback(() => {
    const isSearching = hasSearch && Boolean(searchQuery);

    // Nothing matched across every group/option: show the empty state.
    if (isSearching && filteredItems.length === 0) {
      // role="presentation" keeps the message out of the listbox's
      // accessibility tree (role="listbox" only permits option/group
      // children); the no-results outcome is announced via the
      // result-count live region instead.
      return [/*#__PURE__*/_jsx("div", {
        role: "presentation",
        ...mergeProps(themeProps('selector-empty-state'), {
          className: "x1b2ylru x2b8uid xv1l7n4 x9ynric xcr08ib"
        }),
        children: "No results found"
      }, "empty")];
    }
    let flatIndex = 0;
    const elements = [];
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (isDivider(option)) {
        // While searching, a standalone divider between groups would orphan
        // itself once its neighbors are filtered out, so skip it.
        if (isSearching) {
          continue;
        }
        elements.push(/*#__PURE__*/_jsx(Divider, {
          xstyle: styles.divider
        }, `divider-${i}`));
      } else if (isSection(option)) {
        const sectionItems = [];
        for (const opt of option.options) {
          const normalized = normalizeOption(opt);
          if (isSearching && !optionMatchesQuery(normalized, searchQuery)) {
            continue;
          }
          sectionItems.push(renderItem(normalized, flatIndex));
          flatIndex++;
        }
        // Hide a group entirely (header + wrapper) when none of its items
        // match the query, so no header is left standing over nothing.
        if (sectionItems.length === 0) {
          continue;
        }
        // The heading lives INSIDE the group and is aria-hidden: the group
        // already carries the title as its accessible name, so exposing the
        // text again would announce it twice. This also keeps role="listbox"'s
        // children to option/group only — the old labeled Divider sat in the
        // listbox as a stray role="separator".
        elements.push(/*#__PURE__*/_jsxs("div", {
          role: "group",
          "aria-label": option.title,
          children: [option.title && /*#__PURE__*/_jsx("div", {
            "aria-hidden": "true",
            ...mergeProps(themeProps('selector-section-heading'), {
              className: "xu0wf1k xf314gf x9ynric x141an7d x1ltkj2j xv1l7n4 x87ps6o"
            }),
            children: option.title
          }), sectionItems]
        }, `section-${i}`));
      } else if (isOptionData(option)) {
        const normalized = normalizeOption(option);
        if (isSearching && !optionMatchesQuery(normalized, searchQuery)) {
          continue;
        }
        elements.push(renderItem(normalized, flatIndex));
        flatIndex++;
      }
    }
    return elements;
  }, [options, renderItem, hasSearch, searchQuery, filteredItems]);

  // The detached message box renders its own leading status icon, so the
  // on-field icon would duplicate it — keep the chevron indicator instead.
  const showStatusIcon = status != null && effectiveStatusVariant !== 'detached';
  const showStatusTooltip = status != null && effectiveStatusVariant === 'tooltip' && !!status.message;
  const selectorContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        anchorRef.current = el;
        popover.triggerRef(el);
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, and anchor names
        // compose, so attaching unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      onClick: onTriggerClick,
      "data-testid": testId,
      ...mergeProps(themeProps('selector', {
        variant,
        size,
        status: status?.type ?? null,
        disabled: isDisabled ? 'disabled' : null
      }), stylex.props(inputWrapperStyles.base, styles.triggerContainer, sizeStyles[size], variant === 'ghost' && styles.triggerGhost, variant === 'ghost' && focusOutlineStyles.focusWithin, isDisabled && inputWrapperStyles.disabled, variant === 'ghost' && isDisabled && styles.triggerGhostDisabled, !selectedItem && styles.triggerPlaceholder, variant !== 'ghost' && status && inputStatusBorderStyles[status.type], variant !== 'ghost' && status && !isDisabled && inputStatusHoverShadowStyles[status.type], variant !== 'ghost' && inputGroup && groupStyles.inGroup, xstyle), className, style),
      children: [startIcon && renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
        id: inputLabelId,
        children: label
      }), /*#__PURE__*/_jsx("button", {
        ref: triggerRef,
        id: triggerId,
        type: "button"
        // In hasSearch mode the popup's search input is the combobox (it owns
        // focus + aria-activedescendant, comboboxes-4), so the trigger is a
        // plain button that opens the listbox — not a second combobox.
        ,
        role: hasSearch ? undefined : 'combobox',
        ...rest,
        "aria-haspopup": "listbox",
        "aria-expanded": popover.isOpen,
        "aria-controls": listboxId,
        "aria-activedescendant": !hasSearch && popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
        "aria-describedby": ariaDescribedBy,
        "aria-labelledby": ariaLabelledBy,
        "aria-required": isRequired ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined
        // With a disabledMessage the trigger keeps focusability via
        // aria-disabled so the reason is focus-discoverable; activation is
        // still blocked by the isDisabled guards in useCombobox.
        ,
        disabled: isDisabled && !showsDisabledMessage,
        "aria-disabled": showsDisabledMessage ? 'true' : undefined,
        onKeyDown: handleTriggerKeyDown,
        tabIndex: isDisabled && !showsDisabledMessage ? -1 : 0,
        ...{
          className: "x78zum5 x6s0dn4 x1qughib x1txdalj x1iyjqo2 xs83m0k x1r8uery xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x15bjb6t x1heor9g x1ypdohk x1a2a7pz"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            className: "x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft x1yc453h"
          },
          children: selectedItem?.label ?? placeholder
        })
      }), htmlName != null && /*#__PURE__*/_jsx("input", {
        type: "hidden",
        name: htmlName,
        value: value ?? ''
        // Disabled native controls are excluded from form submission;
        // mirror that for the hidden carrier.
        ,
        disabled: isDisabled
      }), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), hasClear && value != null && !isDisabled && /*#__PURE__*/_jsx(InputClearButton, {
        label: t('@astryx.selector.clearLabel', {
          label
        }),
        onClick: handleClear,
        iconClassName: stableClassName('selector-clear-icon')
      }), showStatusIcon ? showStatusTooltip ? /*#__PURE__*/_jsx("button", {
        ref: statusTooltip.ref,
        type: "button",
        "aria-label": t(STATUS_BUTTON_LABEL_KEY[status.type]),
        "aria-describedby": statusTooltip.describedBy,
        onClick: e => e.stopPropagation(),
        ...stylex.props(focusOutlineStyles.focusVisible, styles.statusButton),
        children: /*#__PURE__*/_jsx(Icon, {
          icon: STATUS_ICON_MAP[status.type],
          size: "sm",
          color: STATUS_ICON_COLOR_MAP[status.type],
          xstyle: styles.triggerIcon
        })
      }) : /*#__PURE__*/_jsx(Icon, {
        icon: STATUS_ICON_MAP[status.type],
        size: "sm",
        color: STATUS_ICON_COLOR_MAP[status.type],
        xstyle: styles.triggerIcon
      }) : /*#__PURE__*/_jsx(Icon, {
        icon: "chevronDown",
        size: "sm",
        color: "secondary"
        // The rotation rides on the glyph, alongside the box and color
        // the wrapper used to provide, so one element carries the mark,
        // its open/closed transform, and the theme target.
        ,
        xstyle: [styles.triggerIcon, styles.triggerIconRotation, popover.isOpen && styles.triggerIconOpen]
        // Stable theme target on the chevron glyph itself, so a theme can
        // restyle just this icon (color, size, hover) — and its
        // open/closed state — via `defineTheme`. Same-element rules in
        // @layer astryx-theme win over the icon's own base color/size,
        // which a button-level target could not reach.
        ,
        ...themeProps('selector-indicator-icon', {
          state: popover.isOpen ? 'expanded' : 'collapsed'
        })
      })]
    }), popover.render(hasSearch ? /*#__PURE__*/_jsxs("div", {
      children: [renderSearch(), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx("div", {
        ref: listboxRef,
        id: listboxId,
        role: "listbox",
        "aria-labelledby": triggerId,
        ...{
          0: {
            className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj x1hc1fzr x1ey1afo"
          },
          1: {
            className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x1hc1fzr x1ey1afo x2hg6jq"
          }
        }[!!(variant !== 'ghost') << 0],
        children: renderOptions()
      })]
    }) : /*#__PURE__*/_jsx("div", {
      ref: listboxRef,
      id: listboxId,
      role: "listbox",
      "aria-labelledby": triggerId,
      ...{
        0: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj x1hc1fzr x1ey1afo"
        },
        2: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x1hc1fzr x1ey1afo x2hg6jq"
        },
        1: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj xg01cxk xq2gx43"
        },
        3: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x2hg6jq xg01cxk xq2gx43"
        }
      }[!!(variant !== 'ghost') << 1 | !!!isPositioned << 0],
      children: renderOptions()
    }), {
      placement: popoverPlacement,
      alignment: 'start',
      // The system's standard menu clearance, except in overlay mode:
      // there the measured negative margin owns the block geometry and
      // the menu is meant to sit on the trigger, not clear it.
      offset: shouldOverlaySelectedItem ? undefined : spacingVars['--spacing-1'],
      xstyle: [styles.popover, layerAnimations[popoverPlacement]],
      style: popoverOffsetStyle
    }), showStatusTooltip && statusTooltip.renderTooltip(status?.message ?? ''), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
  if (inputGroup) {
    return selectorContent;
  }
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: triggerId,
    descriptionID: description ? descriptionId : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: effectiveStatusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: selectorContent
  });
}
Selector.displayName = 'Selector';